import React, { useReducer, useRef, useCallback, useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import useMouse from 'react-use/lib/useMouse';

import {
  ADD_APP,
  DEL_APP,
  FOCUS_APP,
  MINIMIZE_APP,
  TOGGLE_MAXIMIZE_APP,
  FOCUS_ICON,
  SELECT_ICONS,
  FOCUS_DESKTOP,
  START_SELECT,
  END_SELECT,
  POWER_OFF,
  CANCEL_POWER_OFF,
} from './constants/actions';
import { FOCUSING, POWER_STATE } from './constants';
import { defaultIconState, defaultAppState, appSettings } from './apps';
import Modal from './Modal';
import Footer from './Footer';
import Windows from './Windows';
import Icons from './Icons';
import { DashedBox } from 'components';

const initState = {
  apps: defaultAppState,
  nextAppID: defaultAppState.length,
  nextZIndex: defaultAppState.length,
  focusing: FOCUSING.WINDOW,
  icons: defaultIconState,
  selecting: false,
  powerState: POWER_STATE.START,
};
const reducer = (state, action = { type: '' }) => {
  switch (action.type) {
    case ADD_APP:
      const app = state.apps.find(
        _app => _app.component === action.payload.component,
      );
      if (action.payload.multiInstance || !app) {
        return {
          ...state,
          apps: [
            ...state.apps,
            {
              ...action.payload,
              id: state.nextAppID,
              zIndex: state.nextZIndex,
            },
          ],
          nextAppID: state.nextAppID + 1,
          nextZIndex: state.nextZIndex + 1,
          focusing: FOCUSING.WINDOW,
        };
      }
      const apps = state.apps.map(app =>
        app.component === action.payload.component
          ? { ...app, zIndex: state.nextZIndex, minimized: false }
          : app,
      );
      return {
        ...state,
        apps,
        nextZIndex: state.nextZIndex + 1,
        focusing: FOCUSING.WINDOW,
      };
    case DEL_APP:
      if (state.focusing !== FOCUSING.WINDOW) return state;
      return {
        ...state,
        apps: state.apps.filter(app => app.id !== action.payload),
        focusing:
          state.apps.length > 1
            ? FOCUSING.WINDOW
            : state.icons.find(icon => icon.isFocus)
            ? FOCUSING.ICON
            : FOCUSING.DESKTOP,
      };
    case FOCUS_APP: {
      const apps = state.apps.map(app =>
        app.id === action.payload
          ? { ...app, zIndex: state.nextZIndex, minimized: false }
          : app,
      );
      return {
        ...state,
        apps,
        nextZIndex: state.nextZIndex + 1,
        focusing: FOCUSING.WINDOW,
      };
    }
    case MINIMIZE_APP: {
      if (state.focusing !== FOCUSING.WINDOW) return state;
      const apps = state.apps.map(app =>
        app.id === action.payload ? { ...app, minimized: true } : app,
      );
      return {
        ...state,
        apps,
        focusing: FOCUSING.WINDOW,
      };
    }
    case TOGGLE_MAXIMIZE_APP: {
      if (state.focusing !== FOCUSING.WINDOW) return state;
      const apps = state.apps.map(app =>
        app.id === action.payload ? { ...app, maximized: !app.maximized } : app,
      );
      return {
        ...state,
        apps,
        focusing: FOCUSING.WINDOW,
      };
    }
    case FOCUS_ICON: {
      const icons = state.icons.map(icon => ({
        ...icon,
        isFocus: icon.id === action.payload,
      }));
      return {
        ...state,
        focusing: FOCUSING.ICON,
        icons,
      };
    }
    case SELECT_ICONS: {
      const icons = state.icons.map(icon => ({
        ...icon,
        isFocus: action.payload.includes(icon.id),
      }));
      return {
        ...state,
        icons,
        focusing: FOCUSING.ICON,
      };
    }
    case FOCUS_DESKTOP:
      return {
        ...state,
        focusing: FOCUSING.DESKTOP,
        icons: state.icons.map(icon => ({
          ...icon,
          isFocus: false,
        })),
      };
    case START_SELECT:
      return {
        ...state,
        focusing: FOCUSING.DESKTOP,
        icons: state.icons.map(icon => ({
          ...icon,
          isFocus: false,
        })),
        selecting: action.payload,
      };
    case END_SELECT:
      return {
        ...state,
        selecting: null,
      };
    case POWER_OFF:
      return {
        ...state,
        powerState: action.payload,
      };
    case CANCEL_POWER_OFF:
      return {
        ...state,
        powerState: POWER_STATE.START,
      };
    default:
      return state;
  }
};

// Witty custom error messages per broken start menu item
const customErrors = {
  'My Documents': null, // handled properly
  'My Pictures': 'C:\\\nMy Pictures was corrupted by a PowerPoint deck.\nAll vacation photos: overwritten with roadmap slides.',
  'My Music': 'C:\\\nMy Music folder contains only lo-fi beats\nfor sprint planning playlists.\nNo further action required.',
  'My Computer': 'C:\\\nMy Computer is fine.\nYour assumptions, however, may not be.',
  'Control Panel': 'C:\\\nAccess Denied.\nYou don\'t want to see what\'s in there.',
  'Set Program Access and Defaults': 'C:\\\nError: Too many defaults.\nPlease reboot career and try again.',
  'Connect To': 'C:\\\nconnect.exe has encountered a problem.\nHave you tried turning stakeholders off and on again?',
  'Printers and Faxes': 'C:\\\nPrinter not found.\n(Nobody has found the printer since 2019.)',
  'Help and Support': 'C:\\\nHelp is unavailable.\nHave you tried Googling it?\nOr asking the AI that hallucinated 40% of this site?',
  'Search': 'C:\\\nsearch.exe requires more context.\nPlease provide:\n- What you\'re looking for\n- Why you need it\n- Business justification\n- 3 stakeholder approvals',
  'Run...': 'C:\\\nrun.exe: Cannot find program.\n\nBut if you want to run something:\nnpm start (it takes a while)',
  'My Recent Documents': 'C:\\\nRecent documents:\n- final_FINAL_v3.pptx\n- actually_final.pptx\n- SEND_THIS_ONE.pptx\n(All identical. This is fine.)',
};

function WinXP() {
  const [state, dispatch] = useReducer(reducer, initState);
  const ref = useRef(null);
  const mouse = useMouse(ref);
  const focusedAppId = getFocusedAppId();

  // ── Boot Screen ── phases: 'login' | 'loading' | 'done'
  const [bootPhase, setBootPhase] = useState('login');
  const bootDone = bootPhase === 'done';

  function handleLogin() {
    // Play XP startup sound
    const audio = new Audio(`${process.env.PUBLIC_URL}/xp-startup.mp3`);
    audio.play().catch(() => {});
    // Go to loading screen
    setBootPhase('loading');
    // After ~4s loading → show desktop
    setTimeout(() => setBootPhase('done'), 2000);
  }

  // ── Screensaver ──
  const [screensaver, setScreensaver] = useState(false);
  const inactiveTimer = useRef(null);
  const resetInactiveTimer = useCallback(() => {
    if (screensaver) setScreensaver(false);
    clearTimeout(inactiveTimer.current);
    inactiveTimer.current = setTimeout(() => setScreensaver(true), 30000);
  }, [screensaver]);
  useEffect(() => {
    resetInactiveTimer();
    return () => clearTimeout(inactiveTimer.current);
  }, []); // eslint-disable-line

  // Prevent mobile browser back button from navigating away
  useEffect(() => {
    history.pushState(null, '', window.location.href);
    function onPopState() {
      history.pushState(null, '', window.location.href);
    }
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []); // eslint-disable-line

  // ── Shutdown / "Safe to turn off" ──
  const [shutdownDone, setShutdownDone] = useState(false);
  const [funnyShutdown, setFunnyShutdown] = useState(false);
  useEffect(() => {
    if (state.powerState === POWER_STATE.TURN_OFF) {
      const t = setTimeout(() => setShutdownDone(true), 5000);
      return () => clearTimeout(t);
    } else {
      setShutdownDone(false);
    }
  }, [state.powerState]);

  // ── Right-click context menu ──
  const [contextMenu, setContextMenu] = useState(null);
  function onContextMenu(e) {
    if (e.target !== e.currentTarget) return;
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY });
  }
  function closeContextMenu() {
    setContextMenu(null);
  }
  function onContextMenuClick(item) {
    setContextMenu(null);
    if (item === 'Work with Mahip') {
      dispatch({ type: ADD_APP, payload: appSettings['mahip.exe'] });
    } else if (item === 'Properties') {
      dispatch({ type: ADD_APP, payload: appSettings['mahip.exe'] });
    } else if (item === 'Refresh') {
      // no-op, just close menu
    }
  }

  const onFocusApp = useCallback(id => {
    dispatch({ type: FOCUS_APP, payload: id });
  }, []);
  const onMaximizeWindow = useCallback(
    id => {
      if (focusedAppId === id) {
        dispatch({ type: TOGGLE_MAXIMIZE_APP, payload: id });
      }
    },
    [focusedAppId],
  );
  const onMinimizeWindow = useCallback(
    id => {
      if (focusedAppId === id) {
        dispatch({ type: MINIMIZE_APP, payload: id });
      }
    },
    [focusedAppId],
  );
  const onCloseApp = useCallback(
    id => {
      if (focusedAppId === id) {
        dispatch({ type: DEL_APP, payload: id });
      }
    },
    [focusedAppId],
  );
  function onMouseDownFooterApp(id) {
    if (focusedAppId === id) {
      dispatch({ type: MINIMIZE_APP, payload: id });
    } else {
      dispatch({ type: FOCUS_APP, payload: id });
    }
  }
  function onMouseDownIcon(id) {
    dispatch({ type: FOCUS_ICON, payload: id });
  }
  function onDoubleClickIcon(component) {
    const appSetting = Object.values(appSettings).find(
      setting => setting.component === component,
    );
    dispatch({ type: ADD_APP, payload: appSetting });
  }
  function getFocusedAppId() {
    if (state.focusing !== FOCUSING.WINDOW) return -1;
    const focusedApp = [...state.apps]
      .sort((a, b) => b.zIndex - a.zIndex)
      .find(app => !app.minimized);
    return focusedApp ? focusedApp.id : -1;
  }
  function onMouseDownFooter() {
    dispatch({ type: FOCUS_DESKTOP });
  }
  function onClickMenuItem(o) {
    if (o === 'mahip.exe')
      dispatch({ type: ADD_APP, payload: appSettings['mahip.exe'] });
    else if (o === 'experience.log')
      dispatch({ type: ADD_APP, payload: appSettings['experience.log'] });
    else if (o === 'stack_overflow.exe')
      dispatch({ type: ADD_APP, payload: appSettings['stack_overflow.exe'] });
    else if (o === 'Outlook Express')
      dispatch({ type: ADD_APP, payload: appSettings['Outlook Express'] });
    else if (o === 'My Documents')
      dispatch({ type: ADD_APP, payload: appSettings['My Documents'] });
    else if (o === 'Minesweeper')
      dispatch({ type: ADD_APP, payload: appSettings.Minesweeper });
    else if (o === 'Notepad')
      dispatch({ type: ADD_APP, payload: appSettings.Notepad });
    else if (o === 'Winamp')
      dispatch({ type: ADD_APP, payload: appSettings.Winamp });
    else if (o === 'Paint')
      dispatch({ type: ADD_APP, payload: appSettings.Paint });
    else if (o === 'Internet Explorer')
      dispatch({ type: ADD_APP, payload: appSettings['Internet Explorer'] });
    else if (o === 'Claude AI')
      dispatch({ type: ADD_APP, payload: appSettings['Claude AI'] });
    else if (o === 'Super PM Bros.')
      dispatch({ type: ADD_APP, payload: appSettings['Super PM Bros.'] });
    else if (o === 'Road Rash: AI Edition')
      dispatch({ type: ADD_APP, payload: appSettings['Road Rash: AI Edition'] });
    else if (o === 'Flappy Bird Lateral Raises')
      dispatch({ type: ADD_APP, payload: appSettings['Flappy Bird Lateral Raises'] });
    else if (o === 'AgentCraft')
      dispatch({ type: ADD_APP, payload: appSettings.AgentCraft });
    else if (o === 'glossary.exe')
      dispatch({ type: ADD_APP, payload: appSettings['glossary.exe'] });
    else if (o === 'Log Off')
      dispatch({ type: POWER_OFF, payload: POWER_STATE.LOG_OFF });
    else if (o === 'Turn Off Computer')
      setFunnyShutdown(true);
    else {
      // Custom witty error messages
      const customMsg = customErrors[o];
      dispatch({
        type: ADD_APP,
        payload: {
          ...appSettings.Error,
          injectProps: {
            message: customMsg || `C:\\\n${o} is not available.\nHave you tried turning your expectations off and on again?`,
          },
        },
      });
    }
  }
  function onMouseDownDesktop(e) {
    if (e.target === e.currentTarget) {
      setContextMenu(null);
      dispatch({
        type: START_SELECT,
        payload: { x: mouse.docX, y: mouse.docY },
      });
    }
  }
  function onMouseUpDesktop(e) {
    dispatch({ type: END_SELECT });
  }
  const onIconsSelected = useCallback(
    iconIds => {
      dispatch({ type: SELECT_ICONS, payload: iconIds });
    },
    [dispatch],
  );
  function onClickModalButton(text) {
    dispatch({ type: CANCEL_POWER_OFF });
    if (text !== 'Turn Off') {
      dispatch({
        type: ADD_APP,
        payload: appSettings.Error,
      });
    }
  }
  function onModalClose() {
    dispatch({ type: CANCEL_POWER_OFF });
  }

  const handleActivity = useCallback((e) => {
    resetInactiveTimer();
  }, [resetInactiveTimer]);

  return (
    <Container
      ref={ref}
      onMouseUp={onMouseUpDesktop}
      onMouseDown={onMouseDownDesktop}
      onContextMenu={onContextMenu}
      onMouseMove={handleActivity}
      onClick={handleActivity}
      onKeyDown={handleActivity}
      state={state.powerState}
    >
      {/* Boot Screen */}
      {bootPhase === 'login' && <BootScreen onLogin={handleLogin} />}
      {bootPhase === 'loading' && <LoadingScreen />}

      {/* Screensaver */}
      {bootDone && screensaver && (
        <Screensaver onClick={() => { setScreensaver(false); resetInactiveTimer(); }} />
      )}

      {/* Funny Shutdown Confirm Dialog */}
      {funnyShutdown && !shutdownDone && (
        <FunnyShutdownDialog
          onConfirm={() => {
            setFunnyShutdown(false);
            dispatch({ type: POWER_OFF, payload: POWER_STATE.TURN_OFF });
          }}
          onCancel={() => setFunnyShutdown(false)}
        />
      )}

      {/* Shutdown Screen */}
      {shutdownDone && (
        <ShutdownScreen onClick={() => {
          setShutdownDone(false);
          dispatch({ type: CANCEL_POWER_OFF });
        }} />
      )}

      {/* Desktop icons */}
      <Icons
        icons={state.icons}
        onMouseDown={onMouseDownIcon}
        onDoubleClick={onDoubleClickIcon}
        displayFocus={state.focusing === FOCUSING.ICON}
        appSettings={appSettings}
        mouse={mouse}
        selecting={state.selecting}
        setSelectedIcons={onIconsSelected}
      />
      <DashedBox startPos={state.selecting} mouse={mouse} />
      <Windows
        apps={state.apps}
        onMouseDown={onFocusApp}
        onClose={onCloseApp}
        onMinimize={onMinimizeWindow}
        onMaximize={onMaximizeWindow}
        focusedAppId={focusedAppId}
      />
      <Footer
        apps={state.apps}
        onMouseDownApp={onMouseDownFooterApp}
        focusedAppId={focusedAppId}
        onMouseDown={onMouseDownFooter}
        onClickMenuItem={onClickMenuItem}
      />
      {state.powerState !== POWER_STATE.START && !shutdownDone && (
        <Modal
          onClose={onModalClose}
          onClickButton={onClickModalButton}
          mode={state.powerState}
        />
      )}

      {/* Right-click Context Menu */}
      {contextMenu && (
        <ContextMenuOverlay onClick={closeContextMenu}>
          <ContextMenu style={{ left: contextMenu.x, top: contextMenu.y }}>
            <ContextMenuItem onClick={() => onContextMenuClick('Refresh')}>
              Refresh
            </ContextMenuItem>
            <ContextSeparator />
            <ContextMenuItem onClick={() => onContextMenuClick('Work with Mahip')} bold>
              ⭐ Work with Mahip
            </ContextMenuItem>
            <ContextSeparator />
            <ContextMenuItem onClick={() => onContextMenuClick('Properties')}>
              Properties
            </ContextMenuItem>
          </ContextMenu>
        </ContextMenuOverlay>
      )}
    </Container>
  );
}

// ── Boot Screen Component ──
function BootScreen({ onLogin }) {
  return (
    <BootDiv>
      <div className="boot-topbar" />
      <div className="boot-main">
        <div className="boot-left">
          <div className="boot-flag">
            <div className="boot-flag-tl" />
            <div className="boot-flag-tr" />
            <div className="boot-flag-bl" />
            <div className="boot-flag-br" />
          </div>
          <div className="boot-logo-text">
            <div className="boot-logo-microsoft">Microsoft<sup>®</sup></div>
            <div className="boot-logo-windows">Windows<span className="boot-logo-xp">XP</span></div>
          </div>
          <div className="boot-divider" />
          <div className="boot-edition">Professional</div>
          <div className="boot-tagline">To begin, click your user name</div>
        </div>
        <div className="boot-separator" />
        <div className="boot-right">
          <div className="boot-user" onClick={onLogin}>
            <img src={`${process.env.PUBLIC_URL}/profile.png`} alt="Mahip Kakan" className="boot-avatar" />
            <div className="boot-name">Mahip Kakan</div>
          </div>
        </div>
      </div>
      <div className="boot-bottombar">
        <div className="boot-turnoff">
          <div className="boot-turnoff-icon">⏻</div>
          <span>Turn off computer</span>
        </div>
        <div className="boot-footer-right">
          After you log on, you can add or change accounts.<br />
          Just go to Control Panel and click User Accounts.
        </div>
      </div>
    </BootDiv>
  );
}

// ── Loading Screen (between login and desktop) ──
function LoadingScreen() {
  return (
    <LoadingDiv>
      <div className="load-flag">
        <div className="load-flag-tl" />
        <div className="load-flag-tr" />
        <div className="load-flag-bl" />
        <div className="load-flag-br" />
      </div>
      <div className="load-logo-text">
        <div className="load-logo-microsoft">Microsoft<sup>®</sup></div>
        <div className="load-logo-windows">
          Windows<span className="load-logo-xp">XP</span>
        </div>
      </div>
      <div className="load-bar-track">
        <div className="load-bar-fill" />
      </div>
    </LoadingDiv>
  );
}

// ── Screensaver Component ──
function Screensaver({ onClick }) {
  const [pos, setPos] = useState({ x: 100, y: 100 });
  const [vel, setVel] = useState({ dx: 2, dy: 1.5 });
  const [color, setColor] = useState('#fff');
  const colors = ['#ff6b6b','#ffd93d','#6bcb77','#4d96ff','#ff6fff','#fff'];

  useEffect(() => {
    const interval = setInterval(() => {
      setPos(p => {
        let nx = p.x + vel.dx;
        let ny = p.y + vel.dy;
        let ndx = vel.dx;
        let ndy = vel.dy;
        if (nx < 0 || nx > window.innerWidth - 200) {
          ndx = -ndx;
          setColor(colors[Math.floor(Math.random() * colors.length)]);
        }
        if (ny < 0 || ny > window.innerHeight - 60) {
          ndy = -ndy;
          setColor(colors[Math.floor(Math.random() * colors.length)]);
        }
        setVel({ dx: ndx, dy: ndy });
        return { x: Math.max(0, Math.min(nx, window.innerWidth - 200)), y: Math.max(0, Math.min(ny, window.innerHeight - 60)) };
      });
    }, 16);
    return () => clearInterval(interval);
  }, [vel]); // eslint-disable-line

  return (
    <SaverDiv onClick={onClick}>
      <div className="saver-text" style={{ left: pos.x, top: pos.y, color }}>
        AI IS THINKING...<br />So is Mahip. Faster though.
      </div>
      <div className="saver-hint">Click anywhere to dismiss</div>
    </SaverDiv>
  );
}

// ── Funny Shutdown Dialog ──
function FunnyShutdownDialog({ onConfirm, onCancel }) {
  return (
    <FunnyDialogOverlay>
      <FunnyDialogBox>
        <div className="fd-title">⚠️ mahip_xp is about to shut down</div>
        <div className="fd-body">
          <p>Are you <em>absolutely sure</em>?</p>
          <p className="fd-sub">47 unsaved browser tabs will be lost.<br />
          Your stakeholders will not be notified.<br />
          The roadmap will remain unshipped.</p>
          <p className="fd-quote">"Closing this tab will not close your backlog."</p>
        </div>
        <div className="fd-buttons">
          <button className="fd-btn fd-cancel" onClick={onCancel}>
            Actually, let me stay
          </button>
          <button className="fd-btn fd-confirm" onClick={onConfirm}>
            Yes, I'm done here
          </button>
        </div>
      </FunnyDialogBox>
    </FunnyDialogOverlay>
  );
}

const FunnyDialogOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;
const FunnyDialogBox = styled.div`
  background: #ECE9D8;
  border: 2px solid #0054E3;
  border-radius: 4px;
  width: 360px;
  font-family: Tahoma, sans-serif;
  box-shadow: 4px 4px 12px rgba(0,0,0,0.5);
  .fd-title {
    background: linear-gradient(to right, #0054E3, #2080F0);
    color: #fff;
    font-size: 13px;
    font-weight: bold;
    padding: 6px 10px;
    border-radius: 2px 2px 0 0;
  }
  .fd-body {
    padding: 18px 20px 10px;
    font-size: 12px;
    color: #000;
    line-height: 1.7;
    p { margin: 0 0 8px; }
  }
  .fd-sub { color: #444; font-size: 11px; }
  .fd-quote {
    font-style: italic;
    color: #666;
    font-size: 11px;
    border-left: 3px solid #0054E3;
    padding-left: 8px;
    margin-top: 8px !important;
  }
  .fd-buttons {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 10px 16px 14px;
    border-top: 1px solid #ccc;
  }
  .fd-btn {
    font-family: Tahoma, sans-serif;
    font-size: 11px;
    padding: 4px 14px;
    border-radius: 3px;
    cursor: pointer;
    border: 1px solid #888;
  }
  .fd-cancel {
    background: linear-gradient(to bottom, #f5f5f5, #ddd);
    &:hover { background: linear-gradient(to bottom, #fff, #e8e8e8); }
    &:active { background: #ccc; }
  }
  .fd-confirm {
    background: linear-gradient(to bottom, #4a90e2, #1a5fb4);
    color: #fff;
    border-color: #1a5fb4;
    font-weight: bold;
    &:hover { background: linear-gradient(to bottom, #5aa0f0, #2070c4); }
    &:active { background: #1a5fb4; }
  }
`;

// ── Shutdown Screen Component ──
function ShutdownScreen({ onClick }) {
  return (
    <ShutdownDiv onClick={onClick}>
      <div className="shutdown-content">
        <div className="shutdown-title">Windows XP</div>
        <div className="shutdown-msg">It is now safe to turn off your computer.</div>
        <div className="shutdown-sub">(Or collaborate with Mahip. Either works.)</div>
        <div className="shutdown-hint">Click to restart...</div>
      </div>
    </ShutdownDiv>
  );
}

const powerOffAnimation = keyframes`
  0% {
    filter: brightness(1) grayscale(0);
  }
  30% {
    filter: brightness(1) grayscale(0);
  }
  100% {
    filter: brightness(0.6) grayscale(1);
  }
`;
const animation = {
  [POWER_STATE.START]: '',
  [POWER_STATE.TURN_OFF]: powerOffAnimation,
  [POWER_STATE.LOG_OFF]: powerOffAnimation,
};

const Container = styled.div`
  @import url('https://fonts.googleapis.com/css?family=Noto+Sans');
  font-family: Tahoma, 'Noto Sans', sans-serif;
  height: 100%;
  overflow: hidden;
  position: relative;
  background: url(https://i.imgur.com/Zk6TR5k.jpg) no-repeat center center fixed;
  background-size: cover;
  animation: ${({ state }) => animation[state]} 5s forwards;
  *:not(input):not(textarea) {
    user-select: none;
  }
`;

const BootDiv = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  flex-direction: column;
  font-family: Tahoma, sans-serif;

  /* XP login background - blue gradient */
  .boot-topbar {
    height: 10px;
    background: #1a3a8c;
    flex-shrink: 0;
  }
  .boot-main {
    flex: 1;
    background: linear-gradient(to bottom,
      #3a6fc8 0%, #4a80d8 15%, #5590e8 30%,
      #6ba0ee 45%, #7ab0f4 60%, #8abcf8 75%,
      #9ac8fc 90%, #a8d4ff 100%
    );
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0;
    position: relative;
    @media (max-width: 600px) {
      flex-direction: column;
      justify-content: center;
      padding: 24px 20px;
      gap: 24px;
    }
  }
  .boot-left {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    padding-right: 40px;
    gap: 8px;
    flex: 1;
    @media (max-width: 600px) {
      align-items: center;
      padding-right: 0;
      flex: none;
      width: 100%;
    }
    max-width: 420px;
  }
  .boot-flag {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 1fr 1fr;
    width: 44px;
    height: 44px;
    gap: 2px;
    margin-bottom: 4px;
    filter: drop-shadow(0 1px 3px rgba(0,0,0,0.4));
  }
  .boot-flag-tl { background: #e74c3c; border-radius: 2px 0 0 0; }
  .boot-flag-tr { background: #27ae60; border-radius: 0 2px 0 0; }
  .boot-flag-bl { background: #3498db; border-radius: 0 0 0 2px; }
  .boot-flag-br { background: #f39c12; border-radius: 0 0 2px 0; }

  .boot-logo-text {
    text-align: right;
    @media (max-width: 600px) {
      text-align: center;
    }
  }
  .boot-logo-microsoft {
    color: rgba(255,255,255,0.85);
    font-size: 12px;
    font-style: italic;
    letter-spacing: 0.5px;
    line-height: 1;
  }
  .boot-logo-windows {
    color: #fff;
    font-size: 40px;
    font-weight: 300;
    letter-spacing: -1px;
    line-height: 1;
    font-family: 'Franklin Gothic Medium', 'Arial Narrow', Arial, sans-serif;
    text-shadow: 1px 1px 4px rgba(0,0,0,0.3);
    @media (max-width: 600px) {
      font-size: 28px;
    }
  }
  .boot-logo-xp {
    color: #f90;
    font-style: italic;
    font-weight: bold;
    font-size: 40px;
    margin-left: 6px;
    @media (max-width: 600px) {
      font-size: 28px;
    }
  }
  .boot-divider {
    width: 100%;
    height: 1px;
    background: linear-gradient(to right, transparent, rgba(255,255,255,0.8), transparent);
  }
  .boot-edition {
    color: rgba(255,255,255,0.9);
    font-size: 13px;
    font-style: italic;
    letter-spacing: 1px;
    text-align: right;
  }
  .boot-tagline {
    color: #fff;
    font-size: 14px;
    text-align: right;
    text-shadow: 0 1px 2px rgba(0,0,0,0.4);
    margin-top: 8px;
  }

  .boot-separator {
    width: 1px;
    height: 60%;
    background: linear-gradient(to bottom,
      transparent,
      rgba(255,255,255,0.5) 20%,
      rgba(255,255,255,0.5) 80%,
      transparent
    );
    margin: 0 20px;
    flex-shrink: 0;
    @media (max-width: 600px) {
      display: none;
    }
  }

  .boot-right {
    flex: 1;
    max-width: 360px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding-left: 40px;
    @media (max-width: 600px) {
      padding-left: 0;
      max-width: 100%;
      width: 100%;
    }
  }
  .boot-user {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 10px 14px;
    border-radius: 4px;
    cursor: pointer;
    transition: background 0.15s;
    &:hover {
      background: rgba(255,255,255,0.15);
    }
    &:hover .boot-name {
      text-decoration: underline;
    }
  }
  .boot-avatar {
    width: 64px;
    height: 64px;
    border-radius: 4px;
    object-fit: cover;
    object-position: center top;
    border: 2px solid rgba(255,255,255,0.7);
    box-shadow: 0 2px 8px rgba(0,0,0,0.4);
    flex-shrink: 0;
  }
  .boot-name {
    color: #fff;
    font-size: 18px;
    font-weight: bold;
    text-shadow: 0 1px 3px rgba(0,0,0,0.5);
  }

  .boot-bottombar {
    height: 52px;
    background: linear-gradient(to bottom, #1a3a8c, #0f2260);
    border-top: 2px solid #f80;
    display: flex;
    align-items: center;
    padding: 0 20px;
    flex-shrink: 0;
  }
  .boot-turnoff {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #fff;
    font-size: 12px;
    cursor: pointer;
    padding: 4px 10px;
    border-radius: 3px;
    &:hover { background: rgba(255,255,255,0.1); }
  }
  .boot-turnoff-icon {
    width: 28px;
    height: 28px;
    background: radial-gradient(circle, #f80 0%, #c60 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    color: #fff;
    border: 2px solid rgba(255,255,255,0.3);
  }
  .boot-footer-right {
    margin-left: auto;
    color: rgba(255,255,255,0.7);
    font-size: 10px;
    text-align: right;
    line-height: 1.5;
  }
`;

const loadBarAnim = keyframes`
  0%   { transform: translateX(-100%); }
  100% { transform: translateX(350%); }
`;

const LoadingDiv = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10000;
  background: #000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;

  .load-flag {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 1fr 1fr;
    width: 38px;
    height: 38px;
    gap: 2px;
    margin-bottom: 4px;
    filter: drop-shadow(0 0 6px rgba(255,255,255,0.3));
  }
  .load-flag-tl { background: #e74c3c; border-radius: 2px 0 0 0; }
  .load-flag-tr { background: #27ae60; border-radius: 0 2px 0 0; }
  .load-flag-bl { background: #3498db; border-radius: 0 0 0 2px; }
  .load-flag-br { background: #f39c12; border-radius: 0 0 2px 0; }

  .load-logo-text { text-align: center; }
  .load-logo-microsoft {
    color: rgba(255,255,255,0.7);
    font-size: 11px;
    font-style: italic;
    letter-spacing: 0.5px;
    line-height: 1;
  }
  .load-logo-windows {
    color: #fff;
    font-size: 36px;
    font-weight: 300;
    letter-spacing: -1px;
    line-height: 1.1;
    font-family: 'Franklin Gothic Medium', 'Arial Narrow', Arial, sans-serif;
  }
  .load-logo-xp {
    color: #f90;
    font-style: italic;
    font-weight: bold;
    font-size: 36px;
    margin-left: 6px;
  }

  .load-bar-track {
    width: 180px;
    height: 8px;
    background: #111;
    border: 1px solid #555;
    border-radius: 4px;
    overflow: hidden;
    margin-top: 8px;
  }
  .load-bar-fill {
    width: 40%;
    height: 100%;
    background: linear-gradient(to right, #1a6ad0, #4da6ff, #1a6ad0);
    border-radius: 4px;
    animation: ${loadBarAnim} 1.1s infinite linear;
  }
`;

const SaverDiv = styled.div`
  position: fixed;
  inset: 0;
  background: #000;
  z-index: 9999;
  cursor: none;
  .saver-text {
    position: absolute;
    font-size: 32px;
    font-weight: bold;
    font-family: 'Arial Black', sans-serif;
    letter-spacing: 4px;
    text-shadow: 0 0 20px currentColor;
    white-space: nowrap;
    transition: color 0.5s;
  }
  .saver-hint {
    position: absolute;
    bottom: 20px;
    width: 100%;
    text-align: center;
    color: rgba(255,255,255,0.2);
    font-size: 11px;
  }
`;

const ShutdownDiv = styled.div`
  position: fixed;
  inset: 0;
  background: #000e33;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  .shutdown-content {
    text-align: center;
    color: #fff;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
  }
  .shutdown-title {
    font-size: 36px;
    font-weight: bold;
    color: #4a90d9;
    letter-spacing: 2px;
  }
  .shutdown-msg {
    font-size: 20px;
    color: #fff;
  }
  .shutdown-sub {
    font-size: 14px;
    color: rgba(255,255,255,0.6);
    font-style: italic;
  }
  .shutdown-hint {
    margin-top: 20px;
    font-size: 11px;
    color: rgba(255,255,255,0.3);
    animation: blink 1.5s infinite;
  }
  @keyframes blink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.3; }
  }
`;

const ContextMenuOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 8000;
`;

const ContextMenu = styled.div`
  position: fixed;
  background: #fff;
  border: 1px solid #999;
  box-shadow: 2px 2px 6px rgba(0,0,0,0.3);
  z-index: 8001;
  min-width: 160px;
  padding: 2px 0;
  font-size: 11px;
  font-family: Tahoma, sans-serif;
`;

const ContextMenuItem = styled.div`
  padding: 4px 20px 4px 24px;
  cursor: default;
  color: ${({ bold }) => bold ? '#000' : '#000'};
  font-weight: ${({ bold }) => bold ? 'bold' : 'normal'};
  &:hover {
    background: #2f71cd;
    color: #fff;
  }
`;

const ContextSeparator = styled.div`
  height: 1px;
  background: #d0d0d0;
  margin: 2px 0;
`;

export default WinXP;
